/**
 * Opportunities desk (staff side; flag crm.opportunities, the API gates every call): Quixo's own sales desk.
 *  /opportunities          queue: all, mine (I am the account manager), awaiting my approval
 *  /opportunities/:id      requirements, the customer's choice and counter, quotation builder, revision, approvals, send, close
 *  /opportunities/staff    sales staff, reporting line, head of operations and self-approval limits (staff_admin edits)
 * The screens live in features/opportunities (shared with the agency desk in the partner portal); this file is the admin frame.
 */
import { useMemo, type ReactNode } from 'react';
import { bff, BffError } from '../../../lib/bff';
import { OpportunitiesProvider, type OppEnv } from '../../../features/opportunities/env';
import { DeskDetailScreen, DeskQueueScreen, SalesStaffScreen } from '../../../features/opportunities/Desk';
import { useSurface } from '../kit/context';
import { Screen } from '../kit/ui';
import { canOperate, isSuperAdmin } from '../lib/logic';

function QuixoDesk({ children }: { children: ReactNode }) {
  const { t, locale, roles } = useSurface();
  const env = useMemo<OppEnv>(() => ({
    variant: 'quixo', t, locale, roles,
    http: { get: (p) => bff(p), send: (method, p, body) => bff(p, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }) },
    failure: (e) => (e instanceof BffError
      ? { status: e.status, reason: (e.envelope?.error.details as { reason?: string } | undefined)?.reason, message: e.envelope?.error.message }
      : { status: 0 }),
    apiBase: '/v1/opportunities/admin',
    links: { list: '/opportunities', detail: (id) => `/opportunities/${encodeURIComponent(id)}`, staff: '/opportunities/staff' },
    Screen,
    can: { sell: canOperate, assign: canOperate, manage: isSuperAdmin, actAs: isSuperAdmin },
  }), [t, locale, roles]);
  return <OpportunitiesProvider env={env}>{children}</OpportunitiesProvider>;
}

export function OpportunitiesQueuePage() { return <QuixoDesk><DeskQueueScreen /></QuixoDesk>; }
export function OpportunityAdminPage() { return <QuixoDesk><DeskDetailScreen /></QuixoDesk>; }
export function SalesStaffPage() { return <QuixoDesk><SalesStaffScreen /></QuixoDesk>; }
